import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { FormComponentProps } from 'antd/lib/form';
import cn from 'classnames';
import { keys, lowerCase } from 'lodash';
import isNil from 'lodash/isNil';
import moment from 'moment';
import { MdInfo } from 'react-icons/md';
import { FormattedHTMLMessage, FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import stable from 'stable';

import { activityCategoriesSel } from '@modules/lookups/duck/selectors';
import { Form, Input, Radio, S, Select, Tooltip } from '@shared/components';
import {
  assistedEagleProjectId,
  initiativesDic,
  initiativesDicKeys,
  initiativesLookup,
} from '@shared/constants';
import { intl } from '@shared/localization';

import { detailModes } from '../../constants';
import { categoriesAndOrganizationsRequest, loadingModalSel } from '../../duck';
import styles from './ActivityDetails.less';

const FormItem = Form.Item;
const { Option } = Select;
const RadioGroup = Radio.Group;

interface Props extends FormComponentProps {
  detailMode: keyof typeof detailModes;
  sectionTitle: string;
  showCategorySelector: boolean;
  activityTypeId: number;
  disabled: boolean;
  categoryId: number | boolean;
}

const ActivityDetails: React.FC<Props> = ({
  form,
  detailMode,
  sectionTitle,
  showCategorySelector = true,
  activityTypeId,
  disabled,
  categoryId = false,
}) => {
  const allCategories = useSelector(activityCategoriesSel);
  const eagleCategories = useMemo(
    () => allCategories.filter(({ name }) => name.includes('(Eagle Project)')),
    [allCategories],
  );

  function computeIsServiceAssisted() {
    return eagleCategories.some(category => category.id === categoryId);
  }
  const [isServiceAssisted, setIsServiceAssisted] = useState<boolean>(() =>
    computeIsServiceAssisted(),
  );

  const dispatch = useDispatch();

  const requiredRule = {
    required: true,
    message: intl.formatMessage({
      id: 'shared.form.error.isRequired',
    }),
  };

  const isModalLoading = useSelector(loadingModalSel);

  const { resetFields, getFieldDecorator, getFieldValue } = form;
  const isProjectTypeDisabled =
    isModalLoading ||
    isNil(isServiceAssisted) ||
    detailModes.RECORD === detailMode ||
    detailModes.JOIN === detailMode ||
    disabled;

  const selectedInitiatives: string[] = getFieldValue('initiative');

  const nonEagleCategories = useMemo(
    () => allCategories.filter(({ name }) => !name.includes('(Eagle Project)')),
    [allCategories],
  );

  useEffect(() => {
    dispatch(categoriesAndOrganizationsRequest());
  }, [dispatch]);

  useEffect(() => {
    if (!showCategorySelector || form.isFieldsTouched()) return;
    //set initial value to Yes if an eagle project category is already selected
    setIsServiceAssisted(computeIsServiceAssisted());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eagleCategories, categoryId]);

  const onToggleRadio = useCallback(
    e => {
      setIsServiceAssisted(e.target.value);
      resetFields(['categoryId']);
    },
    [setIsServiceAssisted, resetFields],
  );

  const sortedCategories = useMemo(() => {
    //return only relevant categories, depending on prior answer to whether service is related to an eagle project
    let filteredCategories = isServiceAssisted
      ? eagleCategories
      : nonEagleCategories;

    filteredCategories = filteredCategories
      //remove current category and the unused generic assisted-eagle-project category
      .filter(
        ({ actTypeId, id }) =>
          actTypeId === activityTypeId && id !== assistedEagleProjectId,
      )
      //remove expired categories
      .filter(
        ({ expiryDt }) =>
          !expiryDt || (expiryDt && moment(expiryDt).isAfter(moment())),
      );

    // stable sort the results
    return stable(filteredCategories, (a, b) =>
      String(a.name).localeCompare(b.name),
    );
  }, [eagleCategories, nonEagleCategories, activityTypeId, isServiceAssisted]);

  return (
    <React.Fragment>
      <S size="4" bold colored colon>
        {sectionTitle || (
          <FormattedMessage id="activity.ActivityDetails.field.projectDetails" />
        )}
      </S>
      <br />
      {showCategorySelector && (
        <React.Fragment>
          <FormItem
            label={
              <FormattedMessage id="activity.ActivityDetails.field.category.assisted" />
            }
          >
            {getFieldDecorator('assisted', {
              initialValue: isServiceAssisted,
              rules: [requiredRule],
            })(
              <RadioGroup disabled={disabled} onChange={onToggleRadio}>
                <Radio value={false}>
                  <FormattedMessage id="activity.ActivityDetails.field.category.assisted.no" />
                </Radio>
                <Radio value={true}>
                  <FormattedMessage id="activity.ActivityDetails.field.category.assisted.yes" />
                </Radio>
              </RadioGroup>,
            )}
          </FormItem>
          <FormItem required>
            {getFieldDecorator('categoryId', {
              rules: [requiredRule],
            })(
              <Select
                disabled={isProjectTypeDisabled}
                size="large"
                placeholder={
                  <React.Fragment>
                    <FormattedMessage id="activity.ActivityDetails.field.category" />
                    <Tooltip
                      overlayStyle={{ maxWidth: 'unset' }}
                      title={
                        <FormattedHTMLMessage id="activity.ActivityDetails.field.category.tooltip" />
                      }
                    >
                      <MdInfo className={styles.infoIcon} />
                    </Tooltip>
                  </React.Fragment>
                }
                optionFilterProp="text"
                showSearch
              >
                {sortedCategories.map(({ id, name }) => (
                  <Option key={`categories_${id}`} value={id}>
                    {name}
                  </Option>
                ))}
              </Select>,
            )}
          </FormItem>
        </React.Fragment>
      )}
      <FormItem>
        {getFieldDecorator('initiative')(
          <Select
            size="large"
            placeholder={intl.formatMessage({
              id: 'activity.ActivityDetails.field.initiative.placeholder',
            })}
            allowClear
            disabled={disabled}
            mode="multiple"
            showArrow
            optionFilterProp="text"
            showSearch
          >
            {initiativesLookup.map(({ id, name }) => (
              <Option key={`initiatives_${id}`} value={id}>
                {name}
              </Option>
            ))}
          </Select>,
        )}
      </FormItem>
      {initiativesDicKeys.map(iniId => {
        const initiativeToMap = initiativesDic[iniId];
        if (isNil(initiativeToMap?.data)) return null;
        const display = selectedInitiatives?.includes(iniId);
        return keys(initiativeToMap.data).map(iniDataKey => (
          <FormItem
            key={`initiativeData.${iniId}.${iniDataKey}`}
            className={cn({
              [styles.hiddenInput]: !display,
            })}
          >
            {getFieldDecorator(`initiativeData.${iniId}.${iniDataKey}`, {
              rules: [
                ...(display
                  ? [
                      {
                        required: true,
                        message: (
                          <FormattedMessage id="shared.form.error.isRequired" />
                        ),
                      },
                    ]
                  : []),
                {
                  pattern: /^\d+(\.\d{1,2})?$/,
                  message: intl.formatMessage({
                    id: 'activity.ActivityDetails.field.initiative.number.error',
                  }),
                },
              ],
            })(
              <Input
                size="large"
                className={styles.input}
                placeholder={intl.formatMessage({
                  id: `activity.ActivityDetails.field.initiative.${lowerCase(
                    iniId,
                  )}.${iniDataKey}`,
                })}
                rounded
                floatingLabel
              />,
            )}
          </FormItem>
        ));
      })}
    </React.Fragment>
  );
};

export default ActivityDetails;
